import { ReactNode, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  MenuProps,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import {
  CheckCircleIconCustomSize,
  Tooltip,
  Vertical3DotsIcon,
} from "components";

type DropDownMenuProps = Omit<MenuProps, "open">;
export const DropdownMenu = ({
  rowData,
  options,
  onOptionClick,
  sx,
  children,
  menuListSx,
  paperSx,
  menuItemBuilder,
  menuProps,
  menuListItemSx,
  isDisabled = false,
  selectedValue,
  anchorOriginHorizontal,
  disbaleOptionValues,
  selectedCheckIcon = false,
  open = false,
}: {
  rowData?: any;
  options: {
    name?: string;
    value?: string;
    type?: string;
    icon?: any;
    tooltip?: string;
  }[];
  onOptionClick?: (
    optionValue: {
      name?: string;
      value?: string;
      type?: string;
    },
    e: any,
  ) => void;
  sx?: SxProps<Theme>;
  menuItemBuilder?: ({
    item,
    index,
  }: {
    item: any;
    index: number;
  }) => ReactNode;
  menuListSx?: SxProps<Theme>;
  menuListItemSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  /** Pass a function to receive the open state (e.g. for aria-expanded on a custom trigger). */
  children?: ReactNode | ((isOpen: boolean) => ReactNode);
  menuProps?: DropDownMenuProps;
  isDisabled?: boolean;
  selectedValue?: string;
  anchorOriginHorizontal?: "left" | "right";
  disbaleOptionValues?: string[];
  selectedCheckIcon?: boolean;
  open?: boolean;
}) => {
  const theme = useTheme();
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const isOpen = Boolean(optionsAnchorEl) || open;

  const handleClickAway = (e: any, option: any) => {
    e.stopPropagation();
    setOptionsAnchorEl(null);
  };

  const handleOptionClick = (e: any, option: any) => {
    e.stopPropagation();
    setOptionsAnchorEl(null);
    onOptionClick && onOptionClick(option, e);
  };

  return (
    <>
      <Box
        onClick={(e) => {
          if (!isDisabled) {
            e.stopPropagation();
            setOptionsAnchorEl(e.currentTarget);
          }
        }}
        sx={sx}
      >
        {children ? (
          typeof children === "function" ? (
            children(isOpen)
          ) : (
            children
          )
        ) : (
          <IconButton>
            <Vertical3DotsIcon />
          </IconButton>
        )}
      </Box>

      <Menu
        open={isOpen}
        anchorEl={optionsAnchorEl}
        onClose={handleClickAway}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: anchorOriginHorizontal || "right",
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: anchorOriginHorizontal || "right",
        }}
        PaperProps={{
          sx: {
            minWidth: "175px",
            maxWidth: "300px", // Added maxWidth to prevent overly wide menus
            bgcolor: "#FFF",
            borderRadius: "8px",
            boxShadow:
              "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
            border: `1px solid ${theme.palette.neutral["015"]}`,
            ...paperSx,
          },
        }}
        MenuListProps={{
          sx: {
            ...theme.typography["body-small"],
            py: 0, // Remove padding to maximize space
            // width: optionsAnchorEl && optionsAnchorEl.offsetWidth,
            ...menuListSx,
          },
        }}
        {...menuProps}
      >
        {options?.map((option) => {
          const menuItem = (
          <MenuItem
            key={`${option?.value}-${option?.type}-${option.name}`}
            sx={{
              display: "flex",
              flexGrow: 1,
              direction: "row",
              alignItems: "center",
              gap: "8px",
              minWidth: "175px",
              width: "100%",
              maxWidth: "100%",
              whiteSpace: "normal", // Allow text to wrap
              minHeight: "40px", // Ensure consistent height
              px: 2, // Add horizontal padding
              py: 1, // Add vertical padding
              backgroundColor:
                option?.value === selectedValue
                  ? theme.palette.neutral["005"]
                  : "",
              ":hover": {
                bgcolor: theme.palette.neutral["B-005"],
              },
              ...menuListItemSx,
            }}
            disabled={disbaleOptionValues?.includes(option?.value || "")}
            onClick={(e) => {
              e.stopPropagation();
              handleOptionClick(e, option);
            }}
          >
            {menuItemBuilder ? (
              menuItemBuilder({ item: option, index: 0 })
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexGrow: 1,
                    alignItems: "center",
                    minWidth: 0, // Important for text overflow
                  }}
                >
                  {option?.icon && (
                    <Box sx={{ display: "flex", mr: 1 }}>{option.icon}</Box>
                  )}
                  <Box
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {option.name}
                  </Box>
                </Box>
                {selectedCheckIcon && option?.value === selectedValue && (
                  <Box sx={{ ml: 1, flexShrink: 0 }}>
                    <CheckCircleIconCustomSize height="16px" color="success" />
                  </Box>
                )}
              </>
            )}
          </MenuItem>
          );
          if (option?.tooltip) {
            return (
              <Tooltip
                key={`${option?.value}-${option?.type}-${option.name}`}
                title={option.tooltip}
                placement="left"
              >
                <span>{menuItem}</span>
              </Tooltip>
            );
          }
          return menuItem;
        })}
      </Menu>
    </>
  );
};

// import { ReactNode, useState } from "react";
// import {
//   Box,
//   IconButton,
//   Menu,
//   MenuItem,
//   MenuProps,
//   SxProps,
//   Theme,
//   useTheme,
// } from "@mui/material";
// import { CheckCircleIconCustomSize, Vertical3DotsIcon } from "components";

// type DropDownMenuProps = Omit<MenuProps, "open">;
// export const DropdownMenu = ({
//   rowData,
//   options,
//   onOptionClick,
//   sx,
//   children,
//   menuListSx,
//   paperSx,
//   menuItemBuilder,
//   menuProps,
//   menuListItemSx,
//   isDisabled = false,
//   selectedValue,
//   anchorOriginHorizontal,
//   disbaleOptionValues,
//   selectedCheckIcon = false,
// }: {
//   rowData?: any;
//   options: { name?: string; value?: string; type?: string; icon?: any }[];
//   onOptionClick?: (optionValue: {
//     name?: string;
//     value?: string;
//     type?: string;
//   }) => void;
//   sx?: SxProps<Theme>;
//   menuItemBuilder?: ({
//     item,
//     index,
//   }: {
//     item: any;
//     index: number;
//   }) => ReactNode;
//   menuListSx?: SxProps<Theme>;
//   menuListItemSx?: SxProps<Theme>;
//   paperSx?: SxProps<Theme>;
//   children?: ReactNode;
//   menuProps?: DropDownMenuProps;
//   isDisabled?: boolean;
//   selectedValue?: string;
//   anchorOriginHorizontal?: "left" | "right";
//   disbaleOptionValues?: string[];
//   selectedCheckIcon?: boolean;
// }) => {
//   const theme = useTheme();
//   const [optionsAnchorEl, setOptionsAnchorEl] = useState<null | HTMLElement>(
//     null
//   );

//   const handleClickAway = (e: any, option: any) => {
//     e.stopPropagation();
//     setOptionsAnchorEl(null);
//   };

//   const handleOptionClick = (e: any, option: any) => {
//     e.stopPropagation();
//     setOptionsAnchorEl(null);
//     onOptionClick && onOptionClick(option);
//   };

//   return (
//     <>
//       <Box
//         onClick={(e) => {
//           if (!isDisabled) {
//             e.stopPropagation();
//             setOptionsAnchorEl(e.currentTarget);
//           }
//         }}
//         sx={sx}
//       >
//         {children ? (
//           children
//         ) : (
//           <IconButton>
//             <Vertical3DotsIcon />
//           </IconButton>
//         )}
//       </Box>

//       <Menu
//         open={Boolean(optionsAnchorEl)}
//         anchorEl={optionsAnchorEl}
//         onClose={handleClickAway}
//         anchorOrigin={{
//           vertical: "bottom",
//           horizontal: anchorOriginHorizontal || "right",
//         }}
//         transformOrigin={{
//           vertical: -8,
//           horizontal: anchorOriginHorizontal || "right",
//         }}
//         PaperProps={{
//           sx: {
//             minWidth: "175px",
//             bgcolor: "#FFF",
//             borderRadius: "8px",
//             boxShadow:
//               "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
//             border: `1px solid ${theme.palette.neutral["015"]}`,
//             ...paperSx,
//           },
//         }}
//         MenuListProps={{
//           sx: {
//             ...theme.typography["body-small"],
//             width: optionsAnchorEl && optionsAnchorEl.offsetWidth,
//             ...menuListSx,
//           },
//         }}
//         {...menuProps}
//       >
//         {options?.map((option) => (
//           <MenuItem
//             key={option?.type}
//             sx={{
//               display: "flex",
//               flexGrow: 1,
//               direction: "row",
//               alignItems: "center",
//               textTransform: "capitalize",
//               gap: "8px",
//               minWidth: "175px",
//               width: "100%",
//               backgroundColor:
//                 option?.value === selectedValue
//                   ? theme.palette.neutral["005"]
//                   : "",
//               ":hover": {
//                 bgcolor: theme.palette.neutral["B-005"],
//               },
//               ...menuListItemSx,
//               //   borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
//             }}
//             disabled={disbaleOptionValues?.includes(option?.value || "")}
//             onClick={(e) => {
//               e.stopPropagation();
//               handleOptionClick(e, option);
//             }}
//           >
//             {menuItemBuilder ? (
//               menuItemBuilder({ item: option, index: 0 })
//             ) : (
//               <>
//                 <Box
//                   sx={{
//                     display: "flex",
//                     flexGrow: 1,
//                     direction: "row",
//                     alignItems: "center",
//                     textTransform: "capitalize",
//                   }}
//                 >
//                   {option?.icon}
//                   {option.name}
//                 </Box>
//                 {selectedCheckIcon && option?.value === selectedValue && (
//                   <Box sx={{ ml: "-6px" }}>
//                     <CheckCircleIconCustomSize height="16px" color="success" />
//                   </Box>
//                 )}
//               </>
//             )}
//           </MenuItem>
//         ))}
//       </Menu>
//     </>
//   );
// };

// {
//   /* <CheckCircleIconCustomSize height="16px" color="success" />; */
// }
